/**
 * Ladeschirm des ganzen Portals: das CreatorOffice-Logo mittig, ruhig atmend (index.html, #tk-laden). Er steht schon im
 * statischen HTML, damit vor dem ersten Zeichnen nichts Weißes aufblitzt, und gehört nie React: Diese Datei blendet ihn
 * nur aus und ein.
 *
 *  - Erster Start: sichtbar, bis React gezeichnet hat (appGezeichnet) und keine Stelle ihn mehr hält.
 *  - Halten (ladeschirmHalten, Komponente Ladeschirm): Anmeldung und Konto werden noch geladen, eine nachgeladene Seite
 *    (Suspense) ist noch nicht da. Nach dem Start erscheint er erst nach VERZOEGERUNG, damit schnelle Wechsel nicht flackern.
 *  - Ausblenden: kurz weich (AUSBLENDEN, wie die Übergangszeit in index.html), danach hidden; mit reduzierter Bewegung sofort.
 */

export const LADESCHIRM_ID = "tk-laden";
export const VERZOEGERUNG = 200;
export const AUSBLENDEN = 300;

let halter = 0;
let gezeichnet = false;
let zeigen: ReturnType<typeof setTimeout> | null = null;
let weg: ReturnType<typeof setTimeout> | null = null;

function element(): HTMLElement | null {
  const doc = (globalThis as { document?: Document }).document;
  return doc?.getElementById(LADESCHIRM_ID) ?? null;
}

function ruhig(): boolean {
  const mm = (globalThis as { matchMedia?: (q: string) => MediaQueryList }).matchMedia;
  return !!mm && mm("(prefers-reduced-motion: reduce)").matches;
}

/** sichtbar (oder gerade dabei, sich auszublenden: dann zurückholen statt neu einblenden) */
function zustand(e: HTMLElement): "sichtbar" | "geht" | "weg" {
  if (e.hidden) return "weg";
  return e.hasAttribute("data-weg") ? "geht" : "sichtbar";
}

function einblenden() {
  const e = element();
  if (!e) return;
  if (weg) {
    clearTimeout(weg);
    weg = null;
  }
  if (e.hidden) {
    // erst unsichtbar einhängen, dann die Deckkraft übergehen lassen
    e.setAttribute("data-weg", "");
    e.hidden = false;
    void e.offsetWidth;
  }
  e.removeAttribute("data-weg");
  e.setAttribute("aria-busy", "true");
}

function ausblenden() {
  const e = element();
  if (zeigen) {
    clearTimeout(zeigen);
    zeigen = null;
  }
  if (!e || e.hidden) return;
  e.setAttribute("data-weg", "");
  e.setAttribute("aria-busy", "false");
  if (weg) clearTimeout(weg);
  weg = setTimeout(() => {
    weg = null;
    if (halter === 0) e.hidden = true;
  }, ruhig() ? 0 : AUSBLENDEN);
}

/** Hält den Ladeschirm, bis die zurückgegebene Funktion gerufen wird (mehrfaches Rufen schadet nicht). */
export function ladeschirmHalten(): () => void {
  halter++;
  const e = element();
  if (e) {
    const z = zustand(e);
    if (z === "geht") einblenden();
    else if (z === "weg" && !zeigen) {
      zeigen = setTimeout(() => {
        zeigen = null;
        if (halter > 0) einblenden();
      }, VERZOEGERUNG);
    }
  }
  let frei = false;
  return () => {
    if (frei) return;
    frei = true;
    halter = Math.max(0, halter - 1);
    if (halter === 0 && gezeichnet) ausblenden();
  };
}

/** React hat zum ersten Mal gezeichnet (App.tsx). Hält niemand den Ladeschirm, blendet er sich jetzt aus. */
export function appGezeichnet(): void {
  if (gezeichnet) return;
  gezeichnet = true;
  if (halter === 0) ausblenden();
}

/** Nur für Tests: Ausgangszustand. */
export function ladeschirmZuruecksetzen(): void {
  halter = 0;
  gezeichnet = false;
  if (zeigen) clearTimeout(zeigen);
  if (weg) clearTimeout(weg);
  zeigen = null;
  weg = null;
}
